import React, { useCallback, useMemo, useState } from "react";
import {
  Autocomplete,
  Avatar,
  AvatarGroup,
  Box,
  Chip,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  PictureAsPdf as PdfIcon,
  InsertDriveFile as FileIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import {
  TrailerMaintenanceRecordDto,
  MaintenanceInvoiceDto,
  useGetTrailerMaintenanceRecordsQuery,
  useDeleteTrailerMaintenanceRecordMutation,
} from "../../../api/maintenanceApi";
import {
  DataTable,
  createColumn,
  createSearchableColumn,
  Column,
  Action,
} from "../../../components/common/DataTable/DataTable";
import { SearchableColumn } from "../../../components/common/SearchInputs";

interface TrailerMaintenanceRecordsTableProps {
  trailerNames: string[];
  onEdit?: (record: TrailerMaintenanceRecordDto) => void;
}

const formatDate = (value: string | null | undefined): string => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatCurrency = (value: number | null | undefined): string => {
  if (value == null) return "-";
  return `$${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatMiles = (value: number | null | undefined): string => {
  if (value == null) return "-";
  return value.toLocaleString();
};

const isImageType = (ct: string) => ct.startsWith("image/");

const InvoiceAvatar: React.FC<{ invoice: MaintenanceInvoiceDto }> = ({
  invoice,
}) => {
  if (isImageType(invoice.contentType)) {
    return (
      <Tooltip title={invoice.originalFileName}>
        <Avatar
          src={invoice.url}
          variant="rounded"
          sx={{ width: 32, height: 32, cursor: "pointer" }}
          onClick={() => window.open(invoice.url, "_blank")}
        />
      </Tooltip>
    );
  }
  return (
    <Tooltip title={invoice.originalFileName}>
      <Avatar
        variant="rounded"
        sx={{
          width: 32,
          height: 32,
          bgcolor: "action.hover",
          cursor: "pointer",
        }}
        onClick={() => window.open(invoice.url, "_blank")}
      >
        {invoice.contentType === "application/pdf" ? (
          <PdfIcon sx={{ fontSize: 18, color: "error.main" }} />
        ) : (
          <FileIcon sx={{ fontSize: 18, color: "text.secondary" }} />
        )}
      </Avatar>
    </Tooltip>
  );
};

const TrailerMaintenanceRecordsTable: React.FC<
  TrailerMaintenanceRecordsTableProps
> = ({ trailerNames, onEdit }) => {
  const [trailerFilter, setTrailerFilter] = useState("");
  const [searchFilters, setSearchFilters] = useState<Record<string, any>>({});
  const { data: allRecords = [], isLoading } =
    useGetTrailerMaintenanceRecordsQuery();
  const [deleteRecord] = useDeleteTrailerMaintenanceRecordMutation();

  const filteredRecords = useMemo(() => {
    return allRecords.filter((r) => {
      if (trailerFilter && r.trailerName !== trailerFilter) return false;
      const descriptionFilter = searchFilters.description;
      if (
        typeof descriptionFilter === "string" &&
        descriptionFilter &&
        !r.description.toLowerCase().includes(descriptionFilter.toLowerCase())
      ) {
        return false;
      }
      return true;
    });
  }, [allRecords, trailerFilter, searchFilters]);

  const handleSearchChange = useCallback(
    (filters: Record<string, any>) => setSearchFilters(filters),
    []
  );

  const searchableColumns: SearchableColumn[] = useMemo(
    () => [createSearchableColumn("description", "Description", "string")],
    []
  );

  const columns: Column[] = useMemo(
    () => [
      createColumn("trailerName", "Trailer", {
        flex: 1.2,
        minWidth: 150,
        renderCell: (value: string) => (
          <Typography
            variant="body2"
            sx={{ fontSize: "0.8125rem", fontWeight: 500 }}
          >
            {value}
          </Typography>
        ),
      }),
      createColumn("date", "Date", {
        width: 130,
        sortable: true,
        sortType: "date",
        renderCell: (value: string) => (
          <Typography variant="body2" sx={{ fontSize: "0.8125rem" }}>
            {formatDate(value)}
          </Typography>
        ),
      }),
      createColumn("description", "Description", {
        flex: 2.5,
        minWidth: 200,
        renderCell: (value: string) => (
          <Tooltip title={value}>
            <Typography variant="body2" noWrap sx={{ fontSize: "0.8125rem" }}>
              {value}
            </Typography>
          </Tooltip>
        ),
      }),
      createColumn("cost", "Cost", {
        width: 110,
        renderCell: (value: number | null) => (
          <Typography
            variant="body2"
            sx={{ fontSize: "0.8125rem", fontWeight: 500 }}
          >
            {formatCurrency(value)}
          </Typography>
        ),
      }),
      createColumn("mileage", "Mileage", {
        width: 110,
        renderCell: (value: number | null) => (
          <Typography variant="body2" sx={{ fontSize: "0.8125rem" }}>
            {formatMiles(value)}
          </Typography>
        ),
      }),
      createColumn("shopName", "Shop", {
        flex: 1,
        minWidth: 130,
        renderCell: (value: string | null) =>
          value ? (
            <Chip
              label={value}
              size="small"
              variant="outlined"
              sx={{ fontSize: "0.75rem" }}
            />
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.8125rem" }}
            >
              -
            </Typography>
          ),
      }),
      createColumn("invoices", "Invoices", {
        width: 120,
        renderCell: (invoices: MaintenanceInvoiceDto[]) =>
          invoices.length > 0 ? (
            <AvatarGroup
              max={3}
              sx={{ "& .MuiAvatar-root": { width: 32, height: 32 } }}
            >
              {invoices.map((inv) => (
                <InvoiceAvatar key={inv.id} invoice={inv} />
              ))}
            </AvatarGroup>
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.8125rem" }}
            >
              -
            </Typography>
          ),
      }),
    ],
    []
  );

  const toolbar = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, px: 2, py: 1.5 }}>
      <Autocomplete
        size="small"
        options={trailerNames}
        value={trailerFilter || null}
        onChange={(_, newValue) => setTrailerFilter(newValue ?? "")}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Trailer"
            placeholder="Type to search..."
          />
        )}
        sx={{ minWidth: 250 }}
        clearOnEscape
      />
    </Box>
  );

  const handleDelete = async (record: TrailerMaintenanceRecordDto) => {
    if (!window.confirm(`Delete record "${record.description.slice(0, 50)}..."?`))
      return;
    try {
      await deleteRecord(record.id).unwrap();
    } catch (err) {
      console.error("Failed to delete trailer record:", err);
    }
  };

  const actions: Action<TrailerMaintenanceRecordDto>[] = [
    ...(onEdit
      ? [
          {
            icon: <EditIcon />,
            label: "Edit",
            onClick: (record: TrailerMaintenanceRecordDto) => onEdit(record),
          },
        ]
      : []),
    {
      icon: <DeleteIcon />,
      label: "Delete",
      onClick: handleDelete,
      color: "error" as const,
    },
  ];

  return (
    <DataTable
      items={filteredRecords}
      columns={columns}
      isLoading={isLoading}
      totalCount={filteredRecords.length}
      toolbar={toolbar}
      actions={actions}
      fitContentHeight
      enableSearch
      searchableColumns={searchableColumns}
      onSearchChange={handleSearchChange}
      enableSorting
    />
  );
};

export default TrailerMaintenanceRecordsTable;
